import supabase, { setSupabaseToken } from './utils/supabase'
import { useEffect, useRef, useState } from 'react'

import { useAuth0 } from '@auth0/auth0-react'
import * as Sentry from '@sentry/react'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'

import ConversationRouteWrapper from './components/layout/ConversationRouteWrapper'
import { ChatRefsProvider } from './contexts/ChatRefs/ChatRefsProvider'
import useUpdateVh from './hooks/layout/useUpdateVh'
import useVhUnit from './hooks/layout/useVhUnit'
import { useApi } from './hooks/useApi'
import { isPWA } from './main'
import { bindKeyboardAdjustments, saveViewportHeight } from './utils/viewport'
import { useMeStore } from './store/useMeStore'
import { useProjectRealtime } from './hooks/useProjectRealtime'

/**
 * Returns true if the current URL contains Auth0 redirect callback params.
 * This is critical because navigating away from these params before Auth0 processes
 * them will cause login loops.
 *
 * @param {string} search - window.location.search or location.search
 * @returns {boolean}
 */
function hasAuthCallbackParams(search) {
  const sp = new URLSearchParams(search)
  return sp.has('code') && sp.has('state')
}

/**
 * RootRouteGate
 *
 * If we ever land on "/" with ?code&state, do NOT navigate away.
 * Let Auth0Provider process the callback first.
 *
 * @returns {JSX.Element | null}
 */
function RootRouteGate() {
  const location = useLocation()
  if (hasAuthCallbackParams(location.search)) return null
  return <Navigate to="/chat" replace />
}

/**
 * ProtectedApp – the main app component that handles authentication and routing.
 *
 * - Prevents flashing protected UI while auth is unknown / redirect is happening
 * - Kicks off login if user is not authenticated
 * - Primes the backend session via /me
 * - Sets Supabase realtime auth and enables realtime subscriptions
 *
 * @returns {JSX.Element | null}
 */
export default function ProtectedApp() {
  useVhUnit()
  useUpdateVh()

  const location = useLocation()

  useEffect(() => {
    if (!isPWA()) return
    const timer = setTimeout(() => {
      bindKeyboardAdjustments()
    }, 200)
    return () => clearTimeout(timer)
  }, [])

  const {
    isAuthenticated,
    loginWithRedirect,
    isLoading: authLoading,
    getIdTokenClaims,
  } = useAuth0()

  const api = useApi()
  const [isBackendReady, setIsBackendReady] = useState(false)

  /**
   * JWT used by Supabase realtime "private" channels.
   * Set after /me succeeds. Clearing this disables realtime.
   * @type {[string|null, Function]}
   */
  const [supabaseJwt, setSupabaseJwt] = useState(null)

  const timerRef = useRef()
  const authDisabled = import.meta.env.VITE_APP_DISABLE_AUTH0 === 'true'

  const setMe = useMeStore((s) => s.setMe)
  const clearMe = useMeStore((s) => s.clearMe)

  const loginTriggeredRef = useRef(false)

  useEffect(() => {
    if (authDisabled) return
    if (authLoading) return
    if (isAuthenticated) return
    if (hasAuthCallbackParams(location.search)) return

    if (loginTriggeredRef.current) return
    loginTriggeredRef.current = true

    clearMe()
    setSupabaseJwt(null)
    saveViewportHeight()

    loginWithRedirect({ appState: { returnTo: '/chat' } })
  }, [
    authDisabled,
    authLoading,
    isAuthenticated,
    location.search,
    loginWithRedirect,
    clearMe,
  ])

  useEffect(() => {
    if (!isAuthenticated) return

    let cancelled = false

    ;(async () => {
      try {
        const { body } = await api.get('/me', {})
        if (cancelled) return

        setMe(body)

        const token = body.token

        supabase.realtime.setAuth(token)
        setSupabaseToken(token)
        setSupabaseJwt(token)

        const idTokenClaims = await getIdTokenClaims()
        if (cancelled) return

        Sentry.setUser({
          id: idTokenClaims.sub,
          email: idTokenClaims.email,
        })

        setIsBackendReady(true)
      } catch (e) {
        console.error('Failed to prime user with /me:', e)
        setIsBackendReady(true)
      } finally {
        clearTimeout(timerRef.current)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [isAuthenticated, api, getIdTokenClaims, setMe])

  useProjectRealtime({ enabled: !!supabaseJwt && !authDisabled, supabaseJwt })

  const isHandlingCallback = hasAuthCallbackParams(location.search)
  const shouldBlockProtectedUI =
    !authDisabled && (authLoading || isHandlingCallback || !isAuthenticated)

  if (shouldBlockProtectedUI) {
    return null
  }
  // ------------------------------------------------

  return (
    <Routes>
      <Route path="/" element={<RootRouteGate />} />

      <Route
        index
        path="/chat/:conversationId?"
        element={
          <ChatRefsProvider>
            <ConversationRouteWrapper />
          </ChatRefsProvider>
        }
      />
      <Route path="*" element={<Navigate to="/chat" replace />} />
    </Routes>
  )
}
